import "@/index.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
const CartDrawer = lazy(() => import("@/components/cart/CartDrawer"));
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import RevealFooter from "@/components/RevealFooter";
// SQUIDDI-ads - 2026-10-09 · banner cookie: nessun analytics prima del consenso
const CookieBanner = lazy(() => import("@/components/CookieBanner"));
// La Home resta nel pacchetto principale: è la pagina d'ingresso delle
// campagne e caricarla a parte peggiorerebbe il primo disegno.
import Home from "@/pages/Home";
// SQUIDDI-ads - 2026-10-09 · code splitting su tutte le altre route: ogni
// pagina arriva solo quando serve (Suspense unico più sotto).
const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
// SQUIDDI-OPT: Isola le route che importano il configuratore dalla PDP - 2026-09-29
const WeCreateFaldone = lazy(() => import("@/pages/WeCreateFaldone"));
const SquiddiDesigner = lazy(() => import("@/pages/SquiddiDesigner"));
const SquiddiCover = lazy(() => import("@/pages/SquiddiCover"));
// SQUIDDI-OPT: personalizzazione gratuita come pagina a passi, non popup - 2026-10-01
const FreeDesignWizard = lazy(() => import("@/pages/FreeDesignWizard"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Production = lazy(() => import("@/pages/Production"));
const Package = lazy(() => import("@/pages/Package"));
const CatalogWeb = lazy(() => import("@/pages/CatalogWeb"));
const Contatti = lazy(() => import("@/pages/Contatti"));
const Admin = lazy(() => import("@/pages/Admin"));
const DesignerArea = lazy(() => import("@/pages/DesignerArea"));
const Cart = lazy(() => import("@/pages/Cart"));
const MyProjects = lazy(() => import("@/pages/MyProjects"));
const MyOrders = lazy(() => import("@/pages/MyOrders"));
const MyLibrary = lazy(() => import("@/pages/MyLibrary"));
const MyQuotes = lazy(() => import("@/pages/MyQuotes"));
const QuoteRequest = lazy(() => import("@/pages/QuoteRequest"));
const SharedCustomizer = lazy(() => import("@/pages/SharedCustomizer"));
const EditReorder = lazy(() => import("@/pages/EditReorder"));
const Account = lazy(() => import("@/pages/Account"));
const GoogleCallback = lazy(() => import("@/pages/GoogleCallback"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentReturn").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancelled = lazy(() => import("@/pages/PaymentReturn").then((m) => ({ default: m.PaymentCancelled })));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const TechniquesPage = lazy(() => import("@/pages/TechniquesPage"));
// SQUIDDI-chatbot - 2026-10-08 · pallino + popup su tutte le pagine pubbliche.
// SQUIDDI-seo - 2026-10-17 · widget, cassetto carrello e banner cookie non
// servono al primo disegno: arrivano in pacchetti a parte (Suspense dedicato).
const SquiddiChatWidget = lazy(() => import("@/components/chat/SquiddiChatWidget"));
import { AUTO_OPEN_KEY } from "@/constants/chat";
const BubblesDemo = lazy(() => import("@/pages/BubblesDemo"));
const FabricBubbleDemo = lazy(() => import("@/pages/FabricBubbleDemo"));
const MondiBubbleDemo = lazy(() => import("@/pages/MondiBubbleDemo"));
const TechBoardDemo = lazy(() => import("@/pages/TechBoardDemo"));
const BeachFooterDemo = lazy(() => import("@/pages/BeachFooterDemo"));
const TutorialDemo = lazy(() => import("@/pages/TutorialDemo"));
const TecnicheDemo = lazy(() => import("@/pages/TecnicheDemo"));
const DashboardDemo = lazy(() => import("@/pages/DashboardDemo"));
const FaldoneDemo = lazy(() => import("@/pages/FaldoneDemo"));
import { trackVisit } from "@/lib/api";
import { Navigate, useParams } from "react-router-dom";

// Vecchi link del faldone (email, WhatsApp): stesso progetto sul nuovo indirizzo.
const LegacyFaldoneRedirect = () => {
  const { projectId } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/squiddi-designer/faldone/${projectId}${search}${hash}`} replace />;
};

// Disattiva il ripristino automatico dello scroll del browser: su mobile, al
// reload/riapertura, tornava all'ultima posizione e — con le sezioni
// scroll-jacking della Home — apriva la pagina "in fondo". Lo impostiamo a
// livello modulo così è attivo prima del restore del browser al load.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/* Riporta in cima ad ogni cambio pagina (o salta all'ancora #hash se presente). */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) { el.scrollIntoView(); return; }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
}

/* Conteggio visite anonimo: un id casuale nel browser, niente dati personali.
   Salta le pagine di lavoro (admin, produzione, designer). */
function VisitTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (/^\/(admin|produzione|designer)/.test(pathname)) return;
    let visitor = localStorage.getItem("squiddi_visitor");
    if (!visitor) {
      visitor = `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("squiddi_visitor", visitor);
    }
    trackVisit(visitor, pathname).catch(() => {});
  }, [pathname]);
  return null;
}

/* Redirect helper for /personalizzatore/:slug → /prodotto/:slug so we can
   keep the customizer entry-point path consistent. */
function RedirectPersonalizzatore() {
  const { slug } = useParams();
  return <Navigate to={`/prodotto/${slug}`} replace />;
}

/* SQUIDDI-chatbot - 2026-10-08
   La vecchia pagina /ai non esiste più: chi arriva da un link o da un
   segnalibro torna in home e il popup si apre da solo (con l'eventuale ?q=). */
function AiRedirect() {
  const q = new URLSearchParams(useLocation().search).get("q") || "1";
  try { sessionStorage.setItem(AUTO_OPEN_KEY, q); } catch {}
  return <Navigate to="/" replace />;
}

function Layout({ children, noFooter }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      {noFooter
        ? <main className="flex-1">{children}</main>
        : <RevealFooter><main className="flex-1">{children}</main></RevealFooter>}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollManager />
            <VisitTracker />
            <Toaster position="top-center" richColors />
            <Suspense fallback={null}>
              {/* SQUIDDI-OPT: Finestra "capo aggiunto" globale - 2026-06-14 */}
              <CartDrawer />
              {/* SQUIDDI-chatbot - 2026-10-08 · launcher + popup, mai una pagina */}
              <SquiddiChatWidget />
              {/* SQUIDDI-ads - 2026-10-09 · consenso cookie prima di ogni misurazione */}
              <CookieBanner />
            </Suspense>
            <Suspense fallback={<div role="status" data-testid="route-loading" className="min-h-screen grid place-items-center text-[var(--brand)]">Caricamento…</div>}>
            <Routes>
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/shop" element={<Layout><Shop /></Layout>} />
              <Route path="/prodotto/:slug" element={<Layout><ProductDetail /></Layout>} />
              {/* iter359 · Squiddi Designer: servizio gratuito da 25 capi. Sostituisce
                  la pagina Squiddi Designer (pacchetto a pagamento ritirato); i vecchi
                  indirizzi reindirizzano qui. */}
              <Route path="/squiddi-designer" element={<Layout><SquiddiDesigner /></Layout>} />
              <Route path="/squiddi-cover" element={<Layout><SquiddiCover /></Layout>} />
              <Route path="/we-create" element={<Navigate to="/squiddi-designer" replace />} />
              <Route path="/personalizzazione-gratuita/:slug" element={<Layout><FreeDesignWizard /></Layout>} />
              <Route path="/wecreate-2" element={<Navigate to="/squiddi-designer" replace />} />
              {/* Legacy/typo redirects — some external links & older
                  bookmarks reach these paths. Redirect instead of 404. */}
              <Route path="/wecreate" element={<Navigate to="/squiddi-designer" replace />} />
              <Route path="/squiddi-designer/faldone/:projectId" element={<Layout><WeCreateFaldone /></Layout>} />
              <Route path="/wecreate/faldone/:projectId" element={<LegacyFaldoneRedirect />} />
              <Route path="/accedi" element={<Navigate to="/account" replace />} />
              <Route path="/login" element={<Navigate to="/account" replace />} />
              <Route path="/personalizzatore/:slug" element={<RedirectPersonalizzatore />} />
              <Route path="/contatti" element={<Layout><Contatti /></Layout>} />
              <Route path="/carrello" element={<Layout><Cart /></Layout>} />
              <Route path="/i-miei-lavori" element={<Layout><MyProjects /></Layout>} />
              <Route path="/i-miei-ordini" element={<Layout><MyOrders /></Layout>} />
              <Route path="/la-mia-libreria" element={<Layout><MyLibrary /></Layout>} />
              <Route path="/preventivi" element={<Layout><MyQuotes /></Layout>} />
              <Route path="/richiedi-preventivo" element={<Layout><QuoteRequest /></Layout>} />
              <Route path="/i-miei-preventivi" element={<Navigate to="/preventivi" replace />} />
              {/* Old "I miei impianti" route → new unified library page. */}
              <Route path="/i-miei-impianti" element={<Navigate to="/la-mia-libreria?tab=impianti" replace />} />
              <Route path="/catalogo" element={<CatalogWeb />} />
              <Route path="/catalogo/:token" element={<CatalogWeb />} />
              <Route path="/customizer/shared/:token" element={<SharedCustomizer />} />
              <Route path="/riordina/:kind/:id" element={<Layout><EditReorder /></Layout>} />
              <Route path="/account" element={<Layout><Account /></Layout>} />
              <Route path="/auth/google" element={<Layout><GoogleCallback /></Layout>} />
              <Route path="/reimposta-password" element={<Layout><ResetPassword /></Layout>} />
              <Route path="/traccia-ordine" element={<Layout><TrackOrder /></Layout>} />
              <Route path="/pagamento/ok" element={<Layout><PaymentSuccess /></Layout>} />
              <Route path="/pagamento/annullato" element={<Layout><PaymentCancelled /></Layout>} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/preventivi" element={<Admin />} />
              <Route path="/designer" element={<Layout noFooter><DesignerArea /></Layout>} />
              <Route path="/legale/:slug" element={<Layout><LegalPage /></Layout>} />
              <Route path="/legale" element={<Layout><LegalPage /></Layout>} />
              <Route path="/tecniche/:group" element={<Layout><TechniquesPage /></Layout>} />
              <Route path="/ai" element={<AiRedirect />} />
              <Route path="/demo-bolle" element={<BubblesDemo />} />
              <Route path="/demo-tessuto" element={<FabricBubbleDemo />} />
              <Route path="/demo-mondi" element={<MondiBubbleDemo />} />
              <Route path="/demo-tecniche" element={<TechBoardDemo />} />
              <Route path="/demo-spiaggia" element={<BeachFooterDemo />} />
              <Route path="/demo-tutorial" element={<TutorialDemo />} />
              <Route path="/demo-esclusive" element={<TecnicheDemo />} />
              <Route path="/demo-dopo-acquisto" element={<DashboardDemo />} />
              <Route path="/demo-faldone" element={<FaldoneDemo />} />
              {/* iter335 · demo /demo-scheda rimossa: la scheda prodotto resta quella attuale. */}
              <Route path="/tecniche" element={<Navigate to="/tecniche/stampe" replace />} />
              <Route path="/produzione" element={<Production />} />
              <Route path="/pacco/:orderId" element={<Layout><Package /></Layout>} />
              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
